import React, { useState, KeyboardEvent } from 'react'
import classNames from 'classnames'
import styles from './Tabs.module.scss'
import ArrowIcon from 'shared/assets/icons/Accordion/ArrowIcon'

type TabType = {
  id: number
  text: string
  Icon?: React.ElementType
  count?: number
  disabled?: boolean
  ContentComponents?: React.ReactNode
}

type AccordionProps = {
  items: TabType[]
  activeIds?: number[]
  setActiveIds?: (ids: number[]) => void
  singleOpen?: boolean
}

const Tabs: React.FC<AccordionProps> = ({
  items,
  activeIds,
  setActiveIds,
  singleOpen = false,
}) => {
  const [internalActiveIds, setInternalActiveIds] = useState<number[]>([])

  const currentActiveIds = activeIds ?? internalActiveIds
  const setCurrentActiveIds = setActiveIds ?? setInternalActiveIds

  const toggleItem = (id: number, disabled?: boolean) => {
    if (disabled) return

    if (currentActiveIds.includes(id)) {
      setCurrentActiveIds(currentActiveIds.filter((activeId) => activeId !== id))
    } else {
      if (singleOpen) {
        setCurrentActiveIds([id])
      } else {
        setCurrentActiveIds([...currentActiveIds, id])
      }
    }
  }

  // Обработка клавиш Enter и Space для дивов с role="button"
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>, id: number, disabled?: boolean) => {
    if (disabled) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleItem(id, disabled)
    }
  }

  return (
    <div className={styles.accordion}>
      {items.map(({ id, text, Icon, count, disabled, ContentComponents }) => {
        const isActive = currentActiveIds.includes(id)

        return (
          <div
            key={id}
            className={classNames(styles.accordion__item, {
              [styles.disabled]: disabled,
            })}
          >
            <div
              className={styles.accordion__header}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-expanded={isActive}
              aria-controls={`accordion-content-${id}`}
              id={`accordion-header-${id}`}
              onClick={() => toggleItem(id, disabled)}
              onKeyDown={(e) => handleKeyDown(e, id, disabled)}
              aria-disabled={disabled}
            >
              {Icon && <Icon className={styles.accordion__icon} />}
              <span className={styles.accordion__title}>{text}</span>
              {typeof count === 'number' && count > 0 && (
                <span className={styles.accordion__count}>{count}</span>
              )}
              <span className={classNames(styles.accordion__toggleIcon, {
                [styles.accordion__toggleIcon_active]: isActive,
              })}>
                <ArrowIcon />
              </span>
            </div>

            {isActive && (
              <div
                id={`accordion-content-${id}`}
                aria-labelledby={`accordion-header-${id}`}
                className={styles.accordion__content}
              >
                {ContentComponents}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default Tabs
